import { detect } from 'detect-browser';
import produce from 'immer';
import { KeyCode } from 'keyboardevent-codes';
import { StrictExclude } from 'ts-essentials';

import { createPrettyKeyCombination } from '../createPrettyKeyCombination';

const browser = detect();

/** Whether current device is an Apple device. */
export const isAppleDevice = /Mac|iPod|iPhone|iPad/.test(browser?.os ?? '');

/** Whether current device is a Windows device. */
export const isWindowsDevice = /^(Windows|win)/.test(browser?.os ?? '');

export const keyboardModifierKeyAliases = [
  // TODO: [CONV-288] Remove usage of $mod in Habla
  '$mod', // same as `$metaOrCtrl` for backwards compatibility
  '$metaOrCtrl',
  '$controlOrAlt',
] as const;

export const keyboardPcModifierKeyCodes = [
  'Control', // Ctrl on PC
  'Alt',
  'Shift',
] as const;

export const keyboardMacModifierKeyCodes = [
  'Control',
  'Alt', // Option on Mac
  'Shift',
  'Meta',
] as const;

export const keyboardUniversalModifierKeyCodes = [
  ...keyboardModifierKeyAliases,
  'Alt',
  'Control',
  'Shift',
] as const;

export const keyboardModifierKeyCodes = [
  'Alt',
  'Control',
  'Meta',
  'Shift',
] as const;

export const keyboardExtendedModifierKeyCodes = [
  ...keyboardModifierKeyAliases,
  ...keyboardModifierKeyCodes,
] as const;

/** Represents the normative order of modifier key combinations for PC devices. */
const keyboardPcModifierKeyOrder: readonly KeyboardPcModifierKeyCode[] = [
  'Control',
  'Alt',
  'Shift',
];

/**
 * Represents the normative order of modifier key combinations for Mac devices.
 *
 * @see https://developer.apple.com/design/human-interface-guidelines/inputs/keyboards/
 */
const keyboardMacModifierKeyOrder: readonly KeyboardMacModifierKeyCode[] = [
  'Control',
  'Alt',
  'Shift',
  'Meta',
];

export const keyboardModifierKeyOrder = isAppleDevice
  ? keyboardMacModifierKeyOrder
  : keyboardPcModifierKeyOrder;

/** Represents various attributes of keyboard event keys. */
export const keyboardEventKeyAttributes: Partial<
  Record<string, KeyboardEventCodeAttributes>
> = {
  ['`']: { description: 'Backquote' },
  ['-']: { description: 'Minus' },
  ['=']: { description: 'Equal' },
  ['[']: { description: 'Bracket left' },
  [']']: { description: 'Bracket right' },
  ['\\']: { description: 'Backslash' },
  [':']: { description: 'Colon' },
  [';']: { description: 'Semicolon' },
  [`'`]: { description: 'Quote' },
  [',']: { description: 'Comma' },
  ['.']: { description: 'Period' },
  ['/']: { description: 'Forward slash' },
  ['&']: { description: 'Ampersand' },
  ['"']: { description: 'Double quote' },
  ['(']: { description: 'Parenthesis left' },
  [')']: { description: 'Parenthesis right' },
  ['§']: { description: 'Section sign' },
  ['!']: { description: 'Exclamation mark' },
  ['$']: { description: 'Dollar sign' },
  ['#']: { description: 'Number sign' },
  ['^']: { description: 'Circumflex accent' },
  ['*']: { description: 'Asterisk' },
  ['@']: { description: 'Commercial at' },
  ['<']: { description: 'Less than sign' },
  ['>']: { description: 'Greater than sign' },
};

/** Represents various attributes of keyboard event codes. */
export const keyboardEventCodeAttributes: Partial<
  Record<KeyboardEventCodes | 'OS', KeyboardEventCodeAttributes>
> = {
  // Modifiers
  $metaOrCtrl: isAppleDevice
    ? { label: '⌘', description: 'Command' }
    : { label: 'Ctrl' },
  $controlOrAlt: isAppleDevice
    ? { label: '⌥', description: 'Option' }
    : { label: 'Alt' },
  Meta: isAppleDevice
    ? { label: '⌘', description: 'Command' }
    : isWindowsDevice
    ? { label: 'Win' }
    : {},
  Alt: isAppleDevice ? { label: '⌥', description: 'Option' } : {},
  Control: isAppleDevice
    ? { label: '⌃', description: 'Control' }
    : { label: 'Ctrl' },
  Shift: isAppleDevice ? { label: '⇧', description: 'Shift' } : {},
  OS: isWindowsDevice ? { label: 'Win' } : {},

  // Row 1
  Backquote: {
    label: '`',
    description: keyboardEventKeyAttributes['`']?.description,
  },
  Minus: {
    label: '-',
    description: keyboardEventKeyAttributes['-']?.description,
  },
  Equal: {
    label: '=',
    description: keyboardEventKeyAttributes['=']?.description,
  },
  Backspace: isAppleDevice ? { label: '⌫', description: 'Delete' } : {},

  // Row 2
  Tab: isAppleDevice ? { label: '⇥', description: 'Tab' } : { label: 'Tab' },
  BracketLeft: {
    label: '[',
    description: keyboardEventKeyAttributes['[']?.description,
  },
  BracketRight: {
    label: ']',
    description: keyboardEventKeyAttributes[']']?.description,
  },
  Backslash: {
    label: '\\',
    description: keyboardEventKeyAttributes['\\']?.description,
  },

  // Row 3
  CapsLock: isAppleDevice
    ? { label: '⇪', description: 'Caps lock' }
    : { label: 'Caps lock' },
  Semicolon: {
    label: `;`,
    description: keyboardEventKeyAttributes[';']?.description,
  },
  Quote: {
    label: `'`,
    description: keyboardEventKeyAttributes[`'`]?.description,
  },
  Enter: isAppleDevice ? { label: '↵', description: 'Return' } : {},

  // Row 4
  IntlBackslash: { label: '\\', description: 'Intl Backslash' },
  Comma: {
    label: ',',
    description: keyboardEventKeyAttributes[',']?.description,
  },
  Period: {
    label: '.',
    description: keyboardEventKeyAttributes['.']?.description,
  },
  Slash: {
    label: '/',
    description: keyboardEventKeyAttributes['/']?.description,
  },

  // Arrows
  ArrowDown: { label: '⏷', description: 'Arrow down' },
  ArrowLeft: { label: '⏴', description: 'Arrow left' },
  ArrowRight: { label: '⏵', description: 'Arrow right' },
  ArrowUp: { label: '⏶', description: 'Arrow up' },

  // Numpad
  NumpadDivide: { label: 'Numpad /', description: 'Numpad divide' },
  NumpadMultiply: { label: 'Numpad *', description: 'Numpad multiply' },
  NumpadSubtract: { label: 'Numpad -', description: 'Numpad subtract' },
  NumpadAdd: { label: 'Numpad +', description: 'Numpad add' },
  NumpadEnter: isAppleDevice
    ? { label: '⌤', description: 'Enter' }
    : { label: 'Numpad enter' },
  Numpad1: { label: 'Numpad 1' },
  Numpad2: { label: 'Numpad 2' },
  Numpad3: { label: 'Numpad 3' },
  Numpad4: { label: 'Numpad 4' },
  Numpad5: { label: 'Numpad 5' },
  Numpad6: { label: 'Numpad 6' },
  Numpad7: { label: 'Numpad 7' },
  Numpad8: { label: 'Numpad 8' },
  Numpad9: { label: 'Numpad 9' },
  Numpad0: { label: 'Numpad 0' },
  NumpadDecimal: { label: 'Numpad .', description: 'Numpad decimal' }, // we can't infer the correct decimal based on keyboard layout
  NumpadEqual: { label: 'Numpad =', description: 'Numpad equal' },
  NumpadComma: { label: 'Numpad ,', description: 'Numpad comma' },

  // Other
  NumLock: isAppleDevice
    ? { label: '⌧', description: 'Num lock' }
    : { label: 'NumLock' },
  Space: isAppleDevice
    ? { label: '␣', description: 'Space' }
    : { label: 'Space' },
  Home: isAppleDevice ? { label: '↖', description: 'Home' } : {},
  End: isAppleDevice ? { label: '↘', description: 'End' } : {},
  PageUp: isAppleDevice
    ? { label: '⇞', description: 'Page up' }
    : { label: 'Page up' },
  PageDown: isAppleDevice
    ? { label: '⇟', description: 'Page down' }
    : { label: 'Page down' },
  Delete: isAppleDevice
    ? { label: '⌦', description: 'Delete' }
    : { label: 'Delete' },
  Escape: isAppleDevice
    ? { label: '⎋', description: 'Escape' }
    : { label: 'Esc', description: 'Escape' },
};

/**
 * Gets code to key map if the browser support it. The map can be used to
 * correctly determine the pressed key from `event.code`.
 *
 * @example
 *   // { 'KeyQ': 'q' } - US keyboard
 *   // { 'KeyQ : 'a' } - AZERTY keyboard
 */
export async function getCodeKeyMap(): Promise<Record<string, string>> {
  if ('keyboard' in navigator) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const layoutMap = await (navigator as any).keyboard.getLayoutMap();

      return Object.fromEntries(layoutMap.entries()) as Record<string, string>;
    } catch {
      return {};
    }
  }

  return {};
}

/**
 * Gets key combinations where modifiers are normatively ordered.
 *
 * @param keyCombinations The key combinations to order.
 */
export function normativeOrderKeyCombinations<
  TKeyboardKeyCombination extends KeyboardKeyCombination,
>(
  keyCombinations: readonly TKeyboardKeyCombination[],
): readonly TKeyboardKeyCombination[] {
  return keyCombinations.map(normativeOrderKeyCombination);
}

/**
 * Gets key combination where modifiers are normatively ordered.
 *
 * @param keyCombination The key combination to order.
 */
export function normativeOrderKeyCombination<
  TKeyboardKeyCombination extends KeyboardKeyCombination,
>(keyCombination: TKeyboardKeyCombination): TKeyboardKeyCombination {
  const sortBase = <
    TKeyboardKeyCombinationBase extends KeyboardKeyCombinationBase,
  >(
    base?: TKeyboardKeyCombinationBase,
  ): TKeyboardKeyCombinationBase | undefined => {
    if (!base) {
      return undefined;
    }

    const key = base[1];
    const modifiers = [...base[0]].sort(
      (a, b) =>
        keyboardModifierKeyOrder.indexOf(
          a as (typeof keyboardModifierKeyOrder)[number],
        ) -
        keyboardModifierKeyOrder.indexOf(
          b as (typeof keyboardModifierKeyOrder)[number],
        ),
    );

    return [modifiers, key] as unknown as TKeyboardKeyCombinationBase;
  };

  const binding = sortBase(keyCombination.binding);
  const presentation = sortBase(keyCombination.presentation);

  return { binding, presentation } as TKeyboardKeyCombination;
}

/**
 * Normalizes a list of key combinations by replacing aliases with OS specific
 * keys.
 *
 * @param keyCombinations The key combinations to normalize.
 */
export function normalizeKeyCombinations<
  TKeyboardKeyCombination extends KeyboardKeyCombination,
  TNormalizedKeyboardKeyCombination extends KeyboardKeyCombination<KeyboardModifierKeyCode>,
>(
  keyCombinations: readonly TKeyboardKeyCombination[],
): readonly TNormalizedKeyboardKeyCombination[] {
  return keyCombinations.map((keyCombination) =>
    normalizeKeyCombination(keyCombination),
  ) as readonly TNormalizedKeyboardKeyCombination[];
}

/**
 * Normalizes the key combination by replacing aliases with OS specific keys.
 *
 * @param keyCombination The key combination to normalize.
 */
export function normalizeKeyCombination<
  TKeyboardKeyCombination extends KeyboardKeyCombination,
  TNormalizedKeyboardKeyCombination extends KeyboardKeyCombination<KeyboardModifierKeyCode>,
>(keyCombination: TKeyboardKeyCombination): TNormalizedKeyboardKeyCombination {
  return produce(keyCombination, (draft) => {
    draft.binding[0] = draft.binding[0].map(
      (modifier) => normalizedModifierKeys[modifier] ?? modifier,
    );
  }) as unknown as TNormalizedKeyboardKeyCombination;
}

const normalizedModifierKeys: Record<
  KeyboardModifierKeyCodeAlias,
  StrictExclude<
    KeyboardExtendedModifierKeyCode,
    '$controlOrAlt' | '$metaOrCtrl'
  >
> = {
  $controlOrAlt: isAppleDevice ? 'Control' : 'Alt',
  $metaOrCtrl: isAppleDevice ? 'Meta' : 'Control',
  $mod: isAppleDevice ? 'Meta' : 'Control',
};

/** Represents the available extended keyboard modifier keys. */
export type KeyboardExtendedModifierKeyCode =
  (typeof keyboardExtendedModifierKeyCodes)[number];

/** Represents the available keyboard modifier keys. */
export type KeyboardModifierKeyCode = (typeof keyboardModifierKeyCodes)[number];

/** Represents the available modifier keys aliases. */
export type KeyboardModifierKeyCodeAlias =
  (typeof keyboardModifierKeyAliases)[number];

/** Represents the available PC modifier keys. */
export type KeyboardPcModifierKeyCode =
  (typeof keyboardPcModifierKeyCodes)[number];

/** Represents the available Mac modifier keys. */
export type KeyboardMacModifierKeyCode =
  (typeof keyboardMacModifierKeyCodes)[number];

/** Represents the available universal modifier keys. */
export type KeyboardUniversalModifierKeyCode =
  (typeof keyboardUniversalModifierKeyCodes)[number];

/** Represents the base keyboard key combination. */
export type KeyboardKeyCombinationBase<
  TKey extends string = string,
  TKeyboardModifierKeyCode extends KeyboardExtendedModifierKeyCode = KeyboardExtendedModifierKeyCode,
> = [modifiers: readonly TKeyboardModifierKeyCode[], key?: TKey];

/** Represents the keyboard key combination. */
export type KeyboardKeyCombination<
  TKeyboardModifierKeyCode extends KeyboardExtendedModifierKeyCode = KeyboardExtendedModifierKeyCode,
> = {
  /**
   * The binding key combination that is used for registering the `keydown`
   * event with.
   */
  readonly binding: KeyboardKeyCombinationBase<
    KeyCode,
    TKeyboardModifierKeyCode
  >;
  /**
   * The key combination that is used for presentation. If not provided, the
   * {@link KeyboardKeyCombination.binding binding} will be used for
   * presentation.
   */
  readonly presentation?: KeyboardKeyCombinationBase<
    string,
    KeyboardModifierKeyCode
  >;
};

/** Represents the keyboard event codes. */
export type KeyboardEventCodes =
  | Exclude<
      KeyCode,
      | 'AltLeft'
      | 'AltRight'
      | 'ShiftLeft'
      | 'ShiftRight'
      | 'ControlLeft'
      | 'ControlRight'
    >
  | KeyboardExtendedModifierKeyCode;

/** Represents various attributes of a keyboard event code. */
export type KeyboardEventCodeAttributes = {
  /** The label of the code. */
  readonly label?: string;
  /** The description of the code. */
  readonly description?: string;
};

/** Gets a human readable string of array of key combinations . */
export function keyCombinationsToString(
  keyCombinations: readonly KeyboardKeyCombination[],
): string {
  return keyCombinations
    .map((keyCombination) =>
      createPrettyKeyCombination(keyCombination)
        .toString()
        .replaceAll(',', ' '),
    )
    .join(', ');
}
